import React, { useState, useEffect, useCallback } from 'react';

interface Project {
  id: string;
  imageUrl: string;
  altText: string;
  title?: string;
  cost?: string;
  year?: string;
}

interface ProjectShowcaseProps {
  projects: Project[];
  isMobile?: boolean;
  isDesktop?: boolean;
}

const MOBILE_LIMIT = 4;
const DESKTOP_LIMIT = 6;

const EmptyShowcase: React.FC<{ className?: string }> = ({ className }) => (
  <div className={`flex flex-col items-center justify-center gap-2 py-10 text-on-surface-variant ${className ?? ''}`}>
    <span className="material-symbols-outlined text-[40px] opacity-30" data-icon="photo_library">photo_library</span>
    <p className="text-sm">No gallery photos yet.</p>
  </div>
);

// ─── Lightbox ────────────────────────────────────────────────────────────────

interface LightboxProps {
  projects: Project[];
  initialIndex: number;
  onClose: () => void;
}

const Lightbox: React.FC<LightboxProps> = ({ projects, initialIndex, onClose }) => {
  const [index, setIndex] = useState(initialIndex);

  const prev = useCallback(() => setIndex((i) => (i - 1 + projects.length) % projects.length), [projects.length]);
  const next = useCallback(() => setIndex((i) => (i + 1) % projects.length), [projects.length]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, prev, next]);

  const current = projects[index];

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
      onClick={onClose}
    >
      {/* Close */}
      <button
        className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
        onClick={onClose}
        aria-label="Close"
      >
        <span className="material-symbols-outlined text-[32px]">close</span>
      </button>

      {/* Counter */}
      <span className="absolute top-4 left-4 text-white text-sm font-medium">
        {index + 1} / {projects.length}
      </span>

      {/* Prev */}
      {projects.length > 1 && (
        <button
          className="absolute left-4 text-white hover:text-gray-300 transition-colors p-2"
          onClick={(e) => { e.stopPropagation(); prev(); }}
          aria-label="Previous image"
        >
          <span className="material-symbols-outlined text-[40px]">chevron_left</span>
        </button>
      )}

      {/* Image */}
      <div className="max-w-4xl max-h-[85vh] px-16" onClick={(e) => e.stopPropagation()}>
        <img
          src={current.imageUrl}
          alt={current.altText}
          className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
        />
        {current.title && (
          <p className="text-white text-center mt-3 text-sm font-medium">{current.title}</p>
        )}
      </div>

      {/* Next */}
      {projects.length > 1 && (
        <button
          className="absolute right-4 text-white hover:text-gray-300 transition-colors p-2"
          onClick={(e) => { e.stopPropagation(); next(); }}
          aria-label="Next image"
        >
          <span className="material-symbols-outlined text-[40px]">chevron_right</span>
        </button>
      )}
    </div>
  );
};

// ─── Component ───────────────────────────────────────────────────────────────

const ProjectShowcase: React.FC<ProjectShowcaseProps> = ({ projects, isMobile, isDesktop }) => {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const openLightbox = (index: number) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);

  if (isMobile) {
    const visible = projects.slice(0, MOBILE_LIMIT);
    const remaining = projects.length - MOBILE_LIMIT;

    return (
      <section className="px-container-margin mt-stack-xl md:hidden">
        <div className="flex items-center justify-between mb-stack-md">
          <h3 className="font-headline-sm text-headline-sm text-primary">Project Showcase</h3>
          {projects.length > MOBILE_LIMIT && (
            <button
              onClick={() => openLightbox(0)}
              className="text-sm text-primary font-medium hover:underline"
            >
              View all {projects.length}
            </button>
          )}
        </div>

        {projects.length === 0 ? (
          <EmptyShowcase />
        ) : (
          <div className="grid grid-cols-4 grid-rows-2 gap-2 h-64">
            {visible.map((project, index) => (
              <div
                key={project.id}
                onClick={() => openLightbox(index)}
                className={`relative overflow-hidden rounded-lg shadow-sm cursor-pointer ${
                  index === 0 ? 'col-span-2 row-span-2' : 'col-span-1 row-span-1'
                }`}
              >
                <img className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" alt={project.altText} src={project.imageUrl} />
                {/* Overlay showing remaining count on last visible tile */}
                {index === MOBILE_LIMIT - 1 && remaining > 0 && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                    <span className="text-white font-bold text-lg">+{remaining}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {lightboxIndex !== null && (
          <Lightbox projects={projects} initialIndex={lightboxIndex} onClose={closeLightbox} />
        )}
      </section>
    );
  }

  if (isDesktop) {
    const visible = projects.slice(0, DESKTOP_LIMIT);
    const remaining = projects.length - DESKTOP_LIMIT;

    return (
      <section className="bg-surface-container-lowest rounded-xl shadow-sm p-stack-lg border border-outline-variant space-y-stack-lg hidden md:block">
        <div className="flex items-center justify-between">
          <h3 className="font-headline-sm text-headline-sm text-primary">Project Showcase</h3>
          {projects.length > DESKTOP_LIMIT && (
            <button
              onClick={() => openLightbox(0)}
              className="text-sm text-primary font-medium hover:underline"
            >
              View all {projects.length}
            </button>
          )}
        </div>

        {projects.length === 0 ? (
          <EmptyShowcase />
        ) : (
          <div className="grid grid-cols-3 gap-stack-md">
            {visible.map((project, index) => (
              <div
                key={project.id}
                onClick={() => openLightbox(index)}
                className={`relative group cursor-pointer overflow-hidden rounded-lg ${
                  index === 0 ? 'col-span-2 row-span-2' : 'h-40'
                }`}
              >
                <img
                  alt={project.altText}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  src={project.imageUrl}
                />
                {project.title && (
                  <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/60 to-transparent text-white opacity-0 group-hover:opacity-100 transition-opacity">
                    <p className="font-label-bold">{project.title}</p>
                  </div>
                )}
                {/* Overlay showing remaining count on last visible tile */}
                {index === DESKTOP_LIMIT - 1 && remaining > 0 && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center rounded-lg">
                    <span className="text-white font-bold text-xl">+{remaining} more</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {lightboxIndex !== null && (
          <Lightbox projects={projects} initialIndex={lightboxIndex} onClose={closeLightbox} />
        )}
      </section>
    );
  }

  return null;
};

export default ProjectShowcase;
